const privacyContent = {
  en: {
    title: 'Privacy Policy',
    effectiveDate: 'Effective Date: March 4, 2026',
    intro: 'My Personal Plant Inventory ("the App") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information.',
    sections: [
      {
        title: '1. Information We Collect',
        paragraphs: ['We collect the minimum information necessary to provide the App\'s functionality:'],
        items: [
          '<strong>Google Account Information:</strong> When you sign in with Google, we receive your name, email address, and profile picture for authentication and session management.',
          '<strong>Google Drive OAuth Tokens:</strong> If you connect Google Drive, we store encrypted OAuth tokens on our server to maintain your Drive connection.',
          '<strong>App Preferences:</strong> Your display settings are stored locally in your browser and on our server.',
        ],
      },
      {
        title: '2. Information We Do Not Collect',
        paragraphs: ['We do not collect, store, or have access to:'],
        items: [
          'Your plant data, photos, or any content you create in the App.',
          'Your Google Drive contents beyond the "PlantInventory" folder.',
          'Your browsing history, location, contacts, or any other personal data.',
        ],
      },
      {
        title: '3. Where Your Data Is Stored',
        paragraphs: [
          '<strong>Google Drive Mode:</strong> All plant data and photos are stored exclusively in your personal Google Drive account. We never copy or retain this data on our servers.',
          '<strong>Local Mode:</strong> All data is stored in your browser\'s local storage (IndexedDB) on your device. No data leaves your device except when using AI identification, which sends a photo to Google\'s Gemini API.',
        ],
      },
      {
        title: '4. How We Use Your Information',
        items: [
          'Authenticate your identity and manage your session.',
          'Connect to your Google Drive to read and write plant data on your behalf.',
          'Send photos to Google Gemini AI for plant identification (only when you explicitly request it).',
        ],
      },
      {
        title: '5. Third-Party Services',
        paragraphs: ['The App integrates with:'],
        items: [
          '<strong>Google Sign-In</strong> \u2014 for authentication.',
          '<strong>Google Drive API</strong> \u2014 for data storage.',
          '<strong>Google Gemini AI</strong> \u2014 for plant identification.',
        ],
        afterItems: 'We do not sell, share, or provide your data to any other third parties.',
      },
      {
        title: '6. Data Retention',
        paragraphs: ['We retain your session data only while your account is active. If you log out, your session data is deleted from our server. Your plant data in Google Drive or local storage remains under your control.'],
      },
      {
        title: '7. Data Security',
        items: [
          'HTTPS encryption for all data in transit.',
          'Content Security Policy (CSP) to prevent cross-site scripting.',
          'Rate limiting to prevent abuse.',
          'Encrypted storage of OAuth tokens.',
        ],
      },
      {
        title: '8. Your Rights',
        items: [
          'Access, modify, or delete any plant data you have created.',
          'Disconnect Google Drive access at any time through your Google Account settings.',
          'Delete your session and log out at any time.',
          'Use the App in local mode without providing any personal information.',
        ],
      },
      {
        title: '9. Children\'s Privacy',
        paragraphs: ['The App is not directed at children under 13. We do not knowingly collect information from children under 13.'],
      },
      {
        title: '10. Changes to This Policy',
        paragraphs: ['We may update this Privacy Policy from time to time. When we do, we will update the "Effective Date" at the top. Your continued use of the App constitutes acceptance of the revised policy.'],
      },
      {
        title: '11. Contact',
        paragraphs: ['If you have any questions about this Privacy Policy, please contact us through the application.'],
      },
    ],
  },
  es: {
    title: 'Pol\u00edtica de Privacidad',
    effectiveDate: 'Fecha de vigencia: 4 de marzo de 2026',
    intro: 'Mi Inventario Personal de Plantas ("la App") se compromete a proteger tu privacidad. Esta Pol\u00edtica de Privacidad explica c\u00f3mo recopilamos, usamos y protegemos tu informaci\u00f3n.',
    sections: [
      {
        title: '1. Informaci\u00f3n que Recopilamos',
        paragraphs: ['Recopilamos la informaci\u00f3n m\u00ednima necesaria para el funcionamiento de la App:'],
        items: [
          '<strong>Informaci\u00f3n de cuenta de Google:</strong> Al iniciar sesi\u00f3n con Google, recibimos tu nombre, correo electr\u00f3nico y foto de perfil para autenticaci\u00f3n.',
          '<strong>Tokens OAuth de Google Drive:</strong> Si conectas Google Drive, almacenamos tokens OAuth cifrados en nuestro servidor.',
          '<strong>Preferencias de la App:</strong> Tus ajustes de visualizaci\u00f3n se almacenan localmente y en nuestro servidor.',
        ],
      },
      {
        title: '2. Informaci\u00f3n que No Recopilamos',
        paragraphs: ['No recopilamos, almacenamos ni tenemos acceso a:'],
        items: [
          'Tus datos de plantas, fotos o cualquier contenido que crees en la App.',
          'El contenido de tu Google Drive m\u00e1s all\u00e1 de la carpeta "PlantInventory".',
          'Tu historial de navegaci\u00f3n, ubicaci\u00f3n, contactos u otros datos personales.',
        ],
      },
      {
        title: '3. D\u00f3nde se Almacenan tus Datos',
        paragraphs: [
          '<strong>Modo Google Drive:</strong> Todos los datos y fotos de plantas se almacenan exclusivamente en tu cuenta personal de Google Drive.',
          '<strong>Modo Local:</strong> Todos los datos se almacenan en el almacenamiento local de tu navegador (IndexedDB). Ning\u00fan dato sale de tu dispositivo excepto al usar la identificaci\u00f3n por IA.',
        ],
      },
      {
        title: '4. C\u00f3mo Usamos tu Informaci\u00f3n',
        items: [
          'Autenticar tu identidad y gestionar tu sesi\u00f3n.',
          'Conectar con tu Google Drive para leer y escribir datos de plantas.',
          'Enviar fotos a la IA Google Gemini para identificaci\u00f3n de plantas (solo cuando t\u00fa lo solicites).',
        ],
      },
      {
        title: '5. Servicios de Terceros',
        paragraphs: ['La App se integra con:'],
        items: [
          '<strong>Google Sign-In</strong> \u2014 para autenticaci\u00f3n.',
          '<strong>Google Drive API</strong> \u2014 para almacenamiento de datos.',
          '<strong>Google Gemini AI</strong> \u2014 para identificaci\u00f3n de plantas.',
        ],
        afterItems: 'No vendemos, compartimos ni proporcionamos tus datos a terceros.',
      },
      {
        title: '6. Retenci\u00f3n de Datos',
        paragraphs: ['Conservamos tus datos de sesi\u00f3n solo mientras tu cuenta est\u00e9 activa. Tus datos de plantas permanecen bajo tu control.'],
      },
      {
        title: '7. Seguridad de Datos',
        items: [
          'Cifrado HTTPS para todos los datos en tr\u00e1nsito.',
          'Pol\u00edtica de Seguridad de Contenido (CSP) para prevenir scripts maliciosos.',
          'Limitaci\u00f3n de velocidad para prevenir abusos.',
          'Almacenamiento cifrado de tokens OAuth.',
        ],
      },
      {
        title: '8. Tus Derechos',
        items: [
          'Acceder, modificar o eliminar cualquier dato de planta que hayas creado.',
          'Desconectar el acceso a Google Drive en cualquier momento.',
          'Eliminar tu sesi\u00f3n y cerrar sesi\u00f3n en cualquier momento.',
          'Usar la App en modo local sin proporcionar informaci\u00f3n personal.',
        ],
      },
      {
        title: '9. Privacidad Infantil',
        paragraphs: ['La App no est\u00e1 dirigida a menores de 13 a\u00f1os. No recopilamos intencionalmente informaci\u00f3n de menores de 13 a\u00f1os.'],
      },
      {
        title: '10. Cambios en esta Pol\u00edtica',
        paragraphs: ['Podemos actualizar esta Pol\u00edtica de Privacidad. Cuando lo hagamos, actualizaremos la "Fecha de vigencia". El uso continuado constituye aceptaci\u00f3n.'],
      },
      {
        title: '11. Contacto',
        paragraphs: ['Si tienes preguntas sobre esta Pol\u00edtica de Privacidad, cont\u00e1ctanos a trav\u00e9s de la aplicaci\u00f3n.'],
      },
    ],
  },
  fr: {
    title: 'Politique de Confidentialit\u00e9',
    effectiveDate: 'Date d\'entr\u00e9e en vigueur : 4 mars 2026',
    intro: 'Mon Inventaire Personnel de Plantes ("l\'Application") s\'engage \u00e0 prot\u00e9ger votre vie priv\u00e9e. Cette Politique de Confidentialit\u00e9 explique comment nous collectons, utilisons et prot\u00e9geons vos informations.',
    sections: [
      {
        title: '1. Informations que Nous Collectons',
        paragraphs: ['Nous collectons le minimum d\'informations n\u00e9cessaires :'],
        items: [
          '<strong>Informations de compte Google :</strong> Lors de la connexion, nous recevons votre nom, adresse e-mail et photo de profil pour l\'authentification.',
          '<strong>Tokens OAuth Google Drive :</strong> Si vous connectez Google Drive, nous stockons des tokens OAuth chiffr\u00e9s.',
          '<strong>Pr\u00e9f\u00e9rences :</strong> Vos param\u00e8tres sont stock\u00e9s localement et sur notre serveur.',
        ],
      },
      {
        title: '2. Informations que Nous ne Collectons Pas',
        paragraphs: ['Nous ne collectons pas :'],
        items: [
          'Vos donn\u00e9es de plantes, photos ou contenu cr\u00e9\u00e9 dans l\'Application.',
          'Le contenu de votre Google Drive au-del\u00e0 du dossier "PlantInventory".',
          'Votre historique de navigation, localisation, contacts ou autres donn\u00e9es personnelles.',
        ],
      },
      {
        title: '3. O\u00f9 Vos Donn\u00e9es Sont Stock\u00e9es',
        paragraphs: [
          '<strong>Mode Google Drive :</strong> Toutes les donn\u00e9es sont stock\u00e9es exclusivement dans votre compte Google Drive personnel.',
          '<strong>Mode Local :</strong> Toutes les donn\u00e9es sont stock\u00e9es dans le stockage local de votre navigateur (IndexedDB).',
        ],
      },
      {
        title: '4. Comment Nous Utilisons Vos Informations',
        items: [
          'Authentifier votre identit\u00e9 et g\u00e9rer votre session.',
          'Se connecter \u00e0 votre Google Drive pour lire et \u00e9crire des donn\u00e9es.',
          'Envoyer des photos \u00e0 Google Gemini AI pour l\'identification (uniquement sur demande).',
        ],
      },
      {
        title: '5. Services Tiers',
        paragraphs: ['L\'Application s\'int\u00e8gre avec :'],
        items: [
          '<strong>Google Sign-In</strong> \u2014 pour l\'authentification.',
          '<strong>Google Drive API</strong> \u2014 pour le stockage.',
          '<strong>Google Gemini AI</strong> \u2014 pour l\'identification.',
        ],
        afterItems: 'Nous ne vendons ni ne partageons vos donn\u00e9es avec des tiers.',
      },
      {
        title: '6. Conservation des Donn\u00e9es',
        paragraphs: ['Nous conservons vos donn\u00e9es de session uniquement tant que votre compte est actif. Vos donn\u00e9es de plantes restent sous votre contr\u00f4le.'],
      },
      {
        title: '7. S\u00e9curit\u00e9 des Donn\u00e9es',
        items: [
          'Chiffrement HTTPS pour toutes les donn\u00e9es en transit.',
          'Politique de S\u00e9curit\u00e9 du Contenu (CSP).',
          'Limitation du d\u00e9bit pour pr\u00e9venir les abus.',
          'Stockage chiffr\u00e9 des tokens OAuth.',
        ],
      },
      {
        title: '8. Vos Droits',
        items: [
          'Acc\u00e9der, modifier ou supprimer vos donn\u00e9es de plantes.',
          'D\u00e9connecter l\'acc\u00e8s Google Drive \u00e0 tout moment.',
          'Supprimer votre session et vous d\u00e9connecter \u00e0 tout moment.',
          'Utiliser l\'Application en mode local sans fournir d\'informations personnelles.',
        ],
      },
      {
        title: '9. Vie Priv\u00e9e des Enfants',
        paragraphs: ['L\'Application n\'est pas destin\u00e9e aux enfants de moins de 13 ans.'],
      },
      {
        title: '10. Modifications',
        paragraphs: ['Nous pouvons mettre \u00e0 jour cette Politique. Votre utilisation continue constitue une acceptation.'],
      },
      {
        title: '11. Contact',
        paragraphs: ['Pour toute question, contactez-nous via l\'application.'],
      },
    ],
  },
  de: {
    title: 'Datenschutzrichtlinie',
    effectiveDate: 'G\u00fcltig ab: 4. M\u00e4rz 2026',
    intro: 'Mein Pers\u00f6nliches Pflanzeninventar ("die App") verpflichtet sich zum Schutz Ihrer Privatsph\u00e4re. Diese Datenschutzrichtlinie erkl\u00e4rt, wie wir Ihre Informationen erfassen, verwenden und sch\u00fctzen.',
    sections: [
      {
        title: '1. Informationen, die Wir Erfassen',
        paragraphs: ['Wir erfassen nur die minimal notwendigen Informationen:'],
        items: [
          '<strong>Google-Kontoinformationen:</strong> Bei der Anmeldung erhalten wir Ihren Namen, E-Mail-Adresse und Profilbild zur Authentifizierung.',
          '<strong>Google Drive OAuth-Tokens:</strong> Bei Verbindung mit Google Drive speichern wir verschl\u00fcsselte OAuth-Tokens.',
          '<strong>App-Einstellungen:</strong> Ihre Anzeigeeinstellungen werden lokal und auf unserem Server gespeichert.',
        ],
      },
      {
        title: '2. Informationen, die Wir Nicht Erfassen',
        paragraphs: ['Wir erfassen nicht:'],
        items: [
          'Ihre Pflanzendaten, Fotos oder erstellte Inhalte.',
          'Inhalte Ihres Google Drive au\u00dferhalb des Ordners "PlantInventory".',
          'Ihren Browserverlauf, Standort, Kontakte oder andere pers\u00f6nliche Daten.',
        ],
      },
      {
        title: '3. Wo Ihre Daten Gespeichert Werden',
        paragraphs: [
          '<strong>Google Drive-Modus:</strong> Alle Daten werden ausschlie\u00dflich in Ihrem pers\u00f6nlichen Google Drive gespeichert.',
          '<strong>Lokaler Modus:</strong> Alle Daten werden im lokalen Speicher Ihres Browsers (IndexedDB) gespeichert.',
        ],
      },
      {
        title: '4. Wie Wir Ihre Informationen Verwenden',
        items: [
          'Ihre Identit\u00e4t authentifizieren und Ihre Sitzung verwalten.',
          'Mit Ihrem Google Drive verbinden, um Pflanzendaten zu lesen und zu schreiben.',
          'Fotos an Google Gemini AI zur Identifizierung senden (nur auf Ihre Anfrage).',
        ],
      },
      {
        title: '5. Drittanbieter-Dienste',
        paragraphs: ['Die App integriert sich mit:'],
        items: [
          '<strong>Google Sign-In</strong> \u2014 zur Authentifizierung.',
          '<strong>Google Drive API</strong> \u2014 zur Datenspeicherung.',
          '<strong>Google Gemini AI</strong> \u2014 zur Pflanzenidentifikation.',
        ],
        afterItems: 'Wir verkaufen oder teilen Ihre Daten nicht mit Dritten.',
      },
      {
        title: '6. Datenaufbewahrung',
        paragraphs: ['Wir bewahren Sitzungsdaten nur auf, solange Ihr Konto aktiv ist. Ihre Pflanzendaten bleiben unter Ihrer Kontrolle.'],
      },
      {
        title: '7. Datensicherheit',
        items: [
          'HTTPS-Verschl\u00fcsselung f\u00fcr alle Daten.',
          'Content Security Policy (CSP) gegen Cross-Site-Scripting.',
          'Rate-Limiting gegen Missbrauch.',
          'Verschl\u00fcsselte Speicherung von OAuth-Tokens.',
        ],
      },
      {
        title: '8. Ihre Rechte',
        items: [
          'Zugriff, \u00c4nderung oder L\u00f6schung Ihrer Pflanzendaten.',
          'Google Drive-Zugang jederzeit trennen.',
          'Sitzung l\u00f6schen und jederzeit abmelden.',
          'Die App im lokalen Modus ohne pers\u00f6nliche Informationen nutzen.',
        ],
      },
      {
        title: '9. Datenschutz f\u00fcr Kinder',
        paragraphs: ['Die App richtet sich nicht an Kinder unter 13 Jahren.'],
      },
      {
        title: '10. \u00c4nderungen',
        paragraphs: ['Wir k\u00f6nnen diese Richtlinie aktualisieren. Ihre weitere Nutzung gilt als Zustimmung.'],
      },
      {
        title: '11. Kontakt',
        paragraphs: ['Bei Fragen kontaktieren Sie uns \u00fcber die Anwendung.'],
      },
    ],
  },
  pt: {
    title: 'Pol\u00edtica de Privacidade',
    effectiveDate: 'Data de Vig\u00eancia: 4 de mar\u00e7o de 2026',
    intro: 'Meu Invent\u00e1rio Pessoal de Plantas ("o Aplicativo") est\u00e1 comprometido em proteger sua privacidade. Esta Pol\u00edtica de Privacidade explica como coletamos, usamos e protegemos suas informa\u00e7\u00f5es.',
    sections: [
      {
        title: '1. Informa\u00e7\u00f5es que Coletamos',
        paragraphs: ['Coletamos o m\u00ednimo de informa\u00e7\u00f5es necess\u00e1rias para a funcionalidade do Aplicativo:'],
        items: [
          '<strong>Informa\u00e7\u00f5es da Conta Google:</strong> Ao fazer login com o Google, recebemos seu nome, endere\u00e7o de e-mail e foto de perfil para autentica\u00e7\u00e3o e gerenciamento de sess\u00e3o.',
          '<strong>Tokens OAuth do Google Drive:</strong> Se voc\u00ea conectar o Google Drive, armazenamos tokens OAuth criptografados em nosso servidor para manter sua conex\u00e3o com o Drive.',
          '<strong>Prefer\u00eancias do Aplicativo:</strong> Suas configura\u00e7\u00f5es de exibi\u00e7\u00e3o s\u00e3o armazenadas localmente no seu navegador e em nosso servidor.',
        ],
      },
      {
        title: '2. Informa\u00e7\u00f5es que N\u00e3o Coletamos',
        paragraphs: ['N\u00e3o coletamos, armazenamos ou temos acesso a:'],
        items: [
          'Seus dados de plantas, fotos ou qualquer conte\u00fado criado no Aplicativo.',
          'O conte\u00fado do seu Google Drive al\u00e9m da pasta "PlantInventory".',
          'Seu hist\u00f3rico de navega\u00e7\u00e3o, localiza\u00e7\u00e3o, contatos ou quaisquer outros dados pessoais.',
        ],
      },
      {
        title: '3. Onde Seus Dados S\u00e3o Armazenados',
        paragraphs: [
          '<strong>Modo Google Drive:</strong> Todos os dados e fotos de plantas s\u00e3o armazenados exclusivamente na sua conta pessoal do Google Drive. Nunca copiamos ou retemos esses dados em nossos servidores.',
          '<strong>Modo Local:</strong> Todos os dados s\u00e3o armazenados no armazenamento local do seu navegador (IndexedDB) no seu dispositivo. Nenhum dado sai do seu dispositivo, exceto ao usar a identifica\u00e7\u00e3o por IA, que envia uma foto para a API Gemini do Google.',
        ],
      },
      {
        title: '4. Como Usamos Suas Informa\u00e7\u00f5es',
        items: [
          'Autenticar sua identidade e gerenciar sua sess\u00e3o.',
          'Conectar ao seu Google Drive para ler e gravar dados de plantas em seu nome.',
          'Enviar fotos para a IA Google Gemini para identifica\u00e7\u00e3o de plantas (somente quando voc\u00ea solicitar explicitamente).',
        ],
      },
      {
        title: '5. Servi\u00e7os de Terceiros',
        paragraphs: ['O Aplicativo se integra com:'],
        items: [
          '<strong>Google Sign-In</strong> \u2014 para autentica\u00e7\u00e3o.',
          '<strong>Google Drive API</strong> \u2014 para armazenamento de dados.',
          '<strong>Google Gemini AI</strong> \u2014 para identifica\u00e7\u00e3o de plantas.',
        ],
        afterItems: 'N\u00e3o vendemos, compartilhamos ou fornecemos seus dados a terceiros.',
      },
      {
        title: '6. Reten\u00e7\u00e3o de Dados',
        paragraphs: ['Retemos seus dados de sess\u00e3o apenas enquanto sua conta estiver ativa. Se voc\u00ea sair, seus dados de sess\u00e3o s\u00e3o exclu\u00eddos do nosso servidor. Seus dados de plantas no Google Drive ou armazenamento local permanecem sob seu controle.'],
      },
      {
        title: '7. Seguran\u00e7a de Dados',
        items: [
          'Criptografia HTTPS para todos os dados em tr\u00e2nsito.',
          'Pol\u00edtica de Seguran\u00e7a de Conte\u00fado (CSP) para prevenir cross-site scripting.',
          'Limita\u00e7\u00e3o de taxa para prevenir abuso.',
          'Armazenamento criptografado de tokens OAuth.',
        ],
      },
      {
        title: '8. Seus Direitos',
        items: [
          'Acessar, modificar ou excluir quaisquer dados de plantas que voc\u00ea criou.',
          'Desconectar o acesso ao Google Drive a qualquer momento atrav\u00e9s das configura\u00e7\u00f5es da sua conta Google.',
          'Excluir sua sess\u00e3o e sair a qualquer momento.',
          'Usar o Aplicativo no modo local sem fornecer informa\u00e7\u00f5es pessoais.',
        ],
      },
      {
        title: '9. Privacidade Infantil',
        paragraphs: ['O Aplicativo n\u00e3o \u00e9 direcionado a crian\u00e7as menores de 13 anos. N\u00e3o coletamos intencionalmente informa\u00e7\u00f5es de crian\u00e7as menores de 13 anos.'],
      },
      {
        title: '10. Altera\u00e7\u00f5es nesta Pol\u00edtica',
        paragraphs: ['Podemos atualizar esta Pol\u00edtica de Privacidade periodicamente. Quando o fizermos, atualizaremos a "Data de Vig\u00eancia" no topo. O uso continuado do Aplicativo constitui aceita\u00e7\u00e3o da pol\u00edtica revisada.'],
      },
      {
        title: '11. Contato',
        paragraphs: ['Se voc\u00ea tiver alguma d\u00favida sobre esta Pol\u00edtica de Privacidade, entre em contato conosco atrav\u00e9s do aplicativo.'],
      },
    ],
  },
  ja: {
    title: '\u30d7\u30e9\u30a4\u30d0\u30b7\u30fc\u30dd\u30ea\u30b7\u30fc',
    effectiveDate: '\u767a\u52b9\u65e5: 2026\u5e743\u67084\u65e5',
    intro: '\u79c1\u306e\u690d\u7269\u30a4\u30f3\u30d9\u30f3\u30c8\u30ea\uff08\u300c\u672c\u30a2\u30d7\u30ea\u300d\uff09\u306f\u3001\u304a\u5ba2\u69d8\u306e\u30d7\u30e9\u30a4\u30d0\u30b7\u30fc\u4fdd\u8b77\u306b\u52aa\u3081\u3066\u3044\u307e\u3059\u3002\u672c\u30d7\u30e9\u30a4\u30d0\u30b7\u30fc\u30dd\u30ea\u30b7\u30fc\u306f\u3001\u5f53\u793e\u304c\u304a\u5ba2\u69d8\u306e\u60c5\u5831\u3092\u3069\u306e\u3088\u3046\u306b\u53ce\u96c6\u3001\u4f7f\u7528\u3001\u4fdd\u8b77\u3059\u308b\u304b\u3092\u8aac\u660e\u3057\u307e\u3059\u3002',
    sections: [
      {
        title: '1. \u53ce\u96c6\u3059\u308b\u60c5\u5831',
        paragraphs: ['\u30a2\u30d7\u30ea\u306e\u6a5f\u80fd\u3092\u63d0\u4f9b\u3059\u308b\u305f\u3081\u306b\u5fc5\u8981\u306a\u6700\u5c0f\u9650\u306e\u60c5\u5831\u3092\u53ce\u96c6\u3057\u307e\u3059\uff1a'],
        items: [
          '<strong>Google\u30a2\u30ab\u30a6\u30f3\u30c8\u60c5\u5831:</strong> Google\u3067\u30b5\u30a4\u30f3\u30a4\u30f3\u3059\u308b\u3068\u3001\u8a8d\u8a3c\u3068\u30bb\u30c3\u30b7\u30e7\u30f3\u7ba1\u7406\u306e\u305f\u3081\u306b\u304a\u540d\u524d\u3001\u30e1\u30fc\u30eb\u30a2\u30c9\u30ec\u30b9\u3001\u30d7\u30ed\u30d5\u30a3\u30fc\u30eb\u5199\u771f\u3092\u53d7\u3051\u53d6\u308a\u307e\u3059\u3002',
          '<strong>Google Drive OAuth\u30c8\u30fc\u30af\u30f3:</strong> Google Drive\u3092\u63a5\u7d9a\u3059\u308b\u3068\u3001Drive\u63a5\u7d9a\u3092\u7dad\u6301\u3059\u308b\u305f\u3081\u306b\u6697\u53f7\u5316\u3055\u308c\u305fOAuth\u30c8\u30fc\u30af\u30f3\u3092\u30b5\u30fc\u30d0\u30fc\u306b\u4fdd\u5b58\u3057\u307e\u3059\u3002',
          '<strong>\u30a2\u30d7\u30ea\u8a2d\u5b9a:</strong> \u8868\u793a\u8a2d\u5b9a\u306f\u30d6\u30e9\u30a6\u30b6\u306e\u30ed\u30fc\u30ab\u30eb\u30b9\u30c8\u30ec\u30fc\u30b8\u3068\u30b5\u30fc\u30d0\u30fc\u306b\u4fdd\u5b58\u3055\u308c\u307e\u3059\u3002',
        ],
      },
      {
        title: '2. \u53ce\u96c6\u3057\u306a\u3044\u60c5\u5831',
        paragraphs: ['\u4ee5\u4e0b\u306e\u60c5\u5831\u306f\u53ce\u96c6\u3001\u4fdd\u5b58\u3001\u30a2\u30af\u30bb\u30b9\u3057\u307e\u305b\u3093\uff1a'],
        items: [
          '\u30a2\u30d7\u30ea\u3067\u4f5c\u6210\u3057\u305f\u690d\u7269\u30c7\u30fc\u30bf\u3001\u5199\u771f\u3001\u305d\u306e\u4ed6\u306e\u30b3\u30f3\u30c6\u30f3\u30c4\u3002',
          '"PlantInventory"\u30d5\u30a9\u30eb\u30c0\u4ee5\u5916\u306eGoogle Drive\u306e\u5185\u5bb9\u3002',
          '\u95b2\u89a7\u5c65\u6b74\u3001\u4f4d\u7f6e\u60c5\u5831\u3001\u9023\u7d61\u5148\u3001\u305d\u306e\u4ed6\u306e\u500b\u4eba\u30c7\u30fc\u30bf\u3002',
        ],
      },
      {
        title: '3. \u30c7\u30fc\u30bf\u306e\u4fdd\u5b58\u5834\u6240',
        paragraphs: [
          '<strong>Google Drive\u30e2\u30fc\u30c9:</strong> \u3059\u3079\u3066\u306e\u690d\u7269\u30c7\u30fc\u30bf\u3068\u5199\u771f\u306f\u3001\u304a\u5ba2\u69d8\u306e\u500b\u4ebaGoogle Drive\u30a2\u30ab\u30a6\u30f3\u30c8\u306b\u306e\u307f\u4fdd\u5b58\u3055\u308c\u307e\u3059\u3002\u5f53\u793e\u306e\u30b5\u30fc\u30d0\u30fc\u306b\u30b3\u30d4\u30fc\u3084\u4fdd\u6301\u306f\u3057\u307e\u305b\u3093\u3002',
          '<strong>\u30ed\u30fc\u30ab\u30eb\u30e2\u30fc\u30c9:</strong> \u3059\u3079\u3066\u306e\u30c7\u30fc\u30bf\u306f\u30d6\u30e9\u30a6\u30b6\u306e\u30ed\u30fc\u30ab\u30eb\u30b9\u30c8\u30ec\u30fc\u30b8\uff08IndexedDB\uff09\u306b\u4fdd\u5b58\u3055\u308c\u307e\u3059\u3002AI\u8b58\u5225\u4f7f\u7528\u6642\u306b\u5199\u771f\u3092Google Gemini API\u306b\u9001\u4fe1\u3059\u308b\u5834\u5408\u3092\u9664\u304d\u3001\u30c7\u30fc\u30bf\u306f\u30c7\u30d0\u30a4\u30b9\u304b\u3089\u5916\u306b\u51fa\u307e\u305b\u3093\u3002',
        ],
      },
      {
        title: '4. \u60c5\u5831\u306e\u5229\u7528\u65b9\u6cd5',
        items: [
          '\u304a\u5ba2\u69d8\u306e\u8eab\u5143\u8a8d\u8a3c\u3068\u30bb\u30c3\u30b7\u30e7\u30f3\u7ba1\u7406\u3002',
          'Google Drive\u306b\u63a5\u7d9a\u3057\u3066\u690d\u7269\u30c7\u30fc\u30bf\u306e\u8aad\u307f\u66f8\u304d\u3092\u884c\u3046\u3002',
          '\u690d\u7269\u8b58\u5225\u306e\u305f\u3081\u306bGoogle Gemini AI\u306b\u5199\u771f\u3092\u9001\u4fe1\uff08\u304a\u5ba2\u69d8\u304c\u660e\u793a\u7684\u306b\u8981\u6c42\u3057\u305f\u5834\u5408\u306e\u307f\uff09\u3002',
        ],
      },
      {
        title: '5. \u30b5\u30fc\u30c9\u30d1\u30fc\u30c6\u30a3\u30b5\u30fc\u30d3\u30b9',
        paragraphs: ['\u672c\u30a2\u30d7\u30ea\u306f\u4ee5\u4e0b\u3068\u9023\u643a\u3057\u3066\u3044\u307e\u3059\uff1a'],
        items: [
          '<strong>Google Sign-In</strong> \u2014 \u8a8d\u8a3c\u7528\u3002',
          '<strong>Google Drive API</strong> \u2014 \u30c7\u30fc\u30bf\u4fdd\u5b58\u7528\u3002',
          '<strong>Google Gemini AI</strong> \u2014 \u690d\u7269\u8b58\u5225\u7528\u3002',
        ],
        afterItems: '\u5f53\u793e\u306f\u304a\u5ba2\u69d8\u306e\u30c7\u30fc\u30bf\u3092\u4ed6\u306e\u7b2c\u4e09\u8005\u306b\u8ca9\u58f2\u3001\u5171\u6709\u3001\u63d0\u4f9b\u3059\u308b\u3053\u3068\u306f\u3042\u308a\u307e\u305b\u3093\u3002',
      },
      {
        title: '6. \u30c7\u30fc\u30bf\u4fdd\u6301',
        paragraphs: ['\u30bb\u30c3\u30b7\u30e7\u30f3\u30c7\u30fc\u30bf\u306f\u30a2\u30ab\u30a6\u30f3\u30c8\u304c\u30a2\u30af\u30c6\u30a3\u30d6\u306a\u9593\u306e\u307f\u4fdd\u6301\u3057\u307e\u3059\u3002\u30ed\u30b0\u30a2\u30a6\u30c8\u3059\u308b\u3068\u30bb\u30c3\u30b7\u30e7\u30f3\u30c7\u30fc\u30bf\u306f\u30b5\u30fc\u30d0\u30fc\u304b\u3089\u524a\u9664\u3055\u308c\u307e\u3059\u3002Google Drive\u3084\u30ed\u30fc\u30ab\u30eb\u30b9\u30c8\u30ec\u30fc\u30b8\u306e\u690d\u7269\u30c7\u30fc\u30bf\u306f\u304a\u5ba2\u69d8\u306e\u7ba1\u7406\u4e0b\u306b\u3042\u308a\u307e\u3059\u3002'],
      },
      {
        title: '7. \u30c7\u30fc\u30bf\u30bb\u30ad\u30e5\u30ea\u30c6\u30a3',
        items: [
          '\u3059\u3079\u3066\u306e\u30c7\u30fc\u30bf\u8ee2\u9001\u306bHTTPS\u6697\u53f7\u5316\u3002',
          '\u30af\u30ed\u30b9\u30b5\u30a4\u30c8\u30b9\u30af\u30ea\u30d7\u30c6\u30a3\u30f3\u30b0\u9632\u6b62\u306e\u305f\u3081\u306e\u30b3\u30f3\u30c6\u30f3\u30c4\u30bb\u30ad\u30e5\u30ea\u30c6\u30a3\u30dd\u30ea\u30b7\u30fc\uff08CSP\uff09\u3002',
          '\u4e0d\u6b63\u4f7f\u7528\u9632\u6b62\u306e\u305f\u3081\u306e\u30ec\u30fc\u30c8\u5236\u9650\u3002',
          'OAuth\u30c8\u30fc\u30af\u30f3\u306e\u6697\u53f7\u5316\u4fdd\u5b58\u3002',
        ],
      },
      {
        title: '8. \u304a\u5ba2\u69d8\u306e\u6a29\u5229',
        items: [
          '\u4f5c\u6210\u3057\u305f\u690d\u7269\u30c7\u30fc\u30bf\u306e\u30a2\u30af\u30bb\u30b9\u3001\u5909\u66f4\u3001\u524a\u9664\u3002',
          'Google\u30a2\u30ab\u30a6\u30f3\u30c8\u8a2d\u5b9a\u304b\u3089\u3044\u3064\u3067\u3082Google Drive\u30a2\u30af\u30bb\u30b9\u3092\u5207\u65ad\u3002',
          '\u3044\u3064\u3067\u3082\u30bb\u30c3\u30b7\u30e7\u30f3\u3092\u524a\u9664\u3057\u3066\u30ed\u30b0\u30a2\u30a6\u30c8\u3002',
          '\u500b\u4eba\u60c5\u5831\u3092\u63d0\u4f9b\u305b\u305a\u306b\u30ed\u30fc\u30ab\u30eb\u30e2\u30fc\u30c9\u3067\u30a2\u30d7\u30ea\u3092\u4f7f\u7528\u3002',
        ],
      },
      {
        title: '9. \u5150\u7ae5\u306e\u30d7\u30e9\u30a4\u30d0\u30b7\u30fc',
        paragraphs: ['\u672c\u30a2\u30d7\u30ea\u306f13\u6b73\u672a\u6e80\u306e\u5150\u7ae5\u3092\u5bfe\u8c61\u3068\u3057\u3066\u3044\u307e\u305b\u3093\u300213\u6b73\u672a\u6e80\u306e\u5150\u7ae5\u304b\u3089\u610f\u56f3\u7684\u306b\u60c5\u5831\u3092\u53ce\u96c6\u3059\u308b\u3053\u3068\u306f\u3042\u308a\u307e\u305b\u3093\u3002'],
      },
      {
        title: '10. \u672c\u30dd\u30ea\u30b7\u30fc\u306e\u5909\u66f4',
        paragraphs: ['\u672c\u30d7\u30e9\u30a4\u30d0\u30b7\u30fc\u30dd\u30ea\u30b7\u30fc\u306f\u968f\u6642\u66f4\u65b0\u3055\u308c\u308b\u5834\u5408\u304c\u3042\u308a\u307e\u3059\u3002\u66f4\u65b0\u6642\u306b\u306f\u4e0a\u90e8\u306e\u300c\u767a\u52b9\u65e5\u300d\u3092\u66f4\u65b0\u3057\u307e\u3059\u3002\u30a2\u30d7\u30ea\u306e\u7d99\u7d9a\u4f7f\u7528\u306f\u6539\u8a02\u3055\u308c\u305f\u30dd\u30ea\u30b7\u30fc\u306e\u627f\u8afe\u3068\u306a\u308a\u307e\u3059\u3002'],
      },
      {
        title: '11. \u304a\u554f\u3044\u5408\u308f\u305b',
        paragraphs: ['\u672c\u30d7\u30e9\u30a4\u30d0\u30b7\u30fc\u30dd\u30ea\u30b7\u30fc\u306b\u3064\u3044\u3066\u3054\u4e0d\u660e\u306a\u70b9\u304c\u3042\u308c\u3070\u3001\u30a2\u30d7\u30ea\u30b1\u30fc\u30b7\u30e7\u30f3\u3092\u901a\u3058\u3066\u304a\u554f\u3044\u5408\u308f\u305b\u304f\u3060\u3055\u3044\u3002'],
      },
    ],
  },
  ko: {
    title: '\uac1c\uc778\uc815\ubcf4 \ucc98\ub9ac\ubc29\uce68',
    effectiveDate: '\uc2dc\ud589\uc77c: 2026\ub144 3\uc6d4 4\uc77c',
    intro: '\ub098\uc758 \uc2dd\ubb3c \uc778\ubca4\ud1a0\ub9ac("\ubcf8 \uc571")\ub294 \uadc0\ud558\uc758 \uac1c\uc778\uc815\ubcf4 \ubcf4\ud638\ub97c \uc704\ud574 \ub178\ub825\ud558\uace0 \uc788\uc2b5\ub2c8\ub2e4. \ubcf8 \uac1c\uc778\uc815\ubcf4 \ucc98\ub9ac\ubc29\uce68\uc740 \ub2f9\uc0ac\uac00 \uadc0\ud558\uc758 \uc815\ubcf4\ub97c \uc5b4\ub5bb\uac8c \uc218\uc9d1, \uc0ac\uc6a9, \ubcf4\ud638\ud558\ub294\uc9c0 \uc124\uba85\ud569\ub2c8\ub2e4.',
    sections: [
      {
        title: '1. \uc218\uc9d1\ud558\ub294 \uc815\ubcf4',
        paragraphs: ['\uc571\uc758 \uae30\ub2a5\uc744 \uc81c\uacf5\ud558\uae30 \uc704\ud574 \ud544\uc694\ud55c \ucd5c\uc18c\ud55c\uc758 \uc815\ubcf4\ub97c \uc218\uc9d1\ud569\ub2c8\ub2e4:'],
        items: [
          '<strong>Google \uacc4\uc815 \uc815\ubcf4:</strong> Google\ub85c \ub85c\uadf8\uc778\ud558\uba74 \uc778\uc99d \ubc0f \uc138\uc158 \uad00\ub9ac\ub97c \uc704\ud574 \uc774\ub984, \uc774\uba54\uc77c \uc8fc\uc18c, \ud504\ub85c\ud544 \uc0ac\uc9c4\uc744 \uc218\uc2e0\ud569\ub2c8\ub2e4.',
          '<strong>Google Drive OAuth \ud1a0\ud070:</strong> Google Drive\ub97c \uc5f0\uacb0\ud558\uba74 Drive \uc5f0\uacb0\uc744 \uc720\uc9c0\ud558\uae30 \uc704\ud574 \uc554\ud638\ud654\ub41c OAuth \ud1a0\ud070\uc744 \uc11c\ubc84\uc5d0 \uc800\uc7a5\ud569\ub2c8\ub2e4.',
          '<strong>\uc571 \ud658\uacbd\uc124\uc815:</strong> \ub514\uc2a4\ud50c\ub808\uc774 \uc124\uc815\uc740 \ube0c\ub77c\uc6b0\uc800 \ub85c\uceec \uc800\uc7a5\uc18c\uc640 \uc11c\ubc84\uc5d0 \uc800\uc7a5\ub429\ub2c8\ub2e4.',
        ],
      },
      {
        title: '2. \uc218\uc9d1\ud558\uc9c0 \uc54a\ub294 \uc815\ubcf4',
        paragraphs: ['\ub2e4\uc74c \uc815\ubcf4\ub294 \uc218\uc9d1, \uc800\uc7a5 \ub610\ub294 \uc811\uadfc\ud558\uc9c0 \uc54a\uc2b5\ub2c8\ub2e4:'],
        items: [
          '\uc571\uc5d0\uc11c \uc0dd\uc131\ud55c \uc2dd\ubb3c \ub370\uc774\ud130, \uc0ac\uc9c4 \ub610\ub294 \ucf58\ud150\uce20.',
          '"PlantInventory" \ud3f4\ub354 \uc678\uc758 Google Drive \ucf58\ud150\uce20.',
          '\ube0c\ub77c\uc6b0\uc9d5 \uae30\ub85d, \uc704\uce58, \uc5f0\ub77d\ucc98 \ub610\ub294 \uae30\ud0c0 \uac1c\uc778 \ub370\uc774\ud130.',
        ],
      },
      {
        title: '3. \ub370\uc774\ud130 \uc800\uc7a5 \uc704\uce58',
        paragraphs: [
          '<strong>Google Drive \ubaa8\ub4dc:</strong> \ubaa8\ub4e0 \uc2dd\ubb3c \ub370\uc774\ud130\uc640 \uc0ac\uc9c4\uc740 \uadc0\ud558\uc758 \uac1c\uc778 Google Drive \uacc4\uc815\uc5d0\ub9cc \uc800\uc7a5\ub429\ub2c8\ub2e4. \ub2f9\uc0ac \uc11c\ubc84\uc5d0 \ubcf5\uc0ac\ud558\uac70\ub098 \ubcf4\uc720\ud558\uc9c0 \uc54a\uc2b5\ub2c8\ub2e4.',
          '<strong>\ub85c\uceec \ubaa8\ub4dc:</strong> \ubaa8\ub4e0 \ub370\uc774\ud130\ub294 \ube0c\ub77c\uc6b0\uc800\uc758 \ub85c\uceec \uc800\uc7a5\uc18c(IndexedDB)\uc5d0 \uc800\uc7a5\ub429\ub2c8\ub2e4. AI \uc2dd\ubcc4 \uc0ac\uc6a9 \uc2dc \uc0ac\uc9c4\uc744 Google Gemini API\ub85c \ubcf4\ub0b4\ub294 \uacbd\uc6b0\ub97c \uc81c\uc678\ud558\uace0 \ub370\uc774\ud130\ub294 \uae30\uae30\ub97c \ub5a0\ub098\uc9c0 \uc54a\uc2b5\ub2c8\ub2e4.',
        ],
      },
      {
        title: '4. \uc815\ubcf4 \uc0ac\uc6a9 \ubc29\ubc95',
        items: [
          '\uc2e0\uc6d0 \ud655\uc778 \ubc0f \uc138\uc158 \uad00\ub9ac.',
          'Google Drive\uc5d0 \uc5f0\uacb0\ud558\uc5ec \uc2dd\ubb3c \ub370\uc774\ud130 \uc77d\uae30/\uc4f0\uae30.',
          '\uc2dd\ubb3c \uc2dd\ubcc4\uc744 \uc704\ud574 Google Gemini AI\uc5d0 \uc0ac\uc9c4 \uc804\uc1a1(\uadc0\ud558\uac00 \uba85\uc2dc\uc801\uc73c\ub85c \uc694\uccad\ud55c \uacbd\uc6b0\uc5d0\ub9cc).',
        ],
      },
      {
        title: '5. \uc81c3\uc790 \uc11c\ube44\uc2a4',
        paragraphs: ['\ubcf8 \uc571\uc740 \ub2e4\uc74c\uacfc \ud1b5\ud569\ub429\ub2c8\ub2e4:'],
        items: [
          '<strong>Google Sign-In</strong> \u2014 \uc778\uc99d\uc6a9.',
          '<strong>Google Drive API</strong> \u2014 \ub370\uc774\ud130 \uc800\uc7a5\uc6a9.',
          '<strong>Google Gemini AI</strong> \u2014 \uc2dd\ubb3c \uc2dd\ubcc4\uc6a9.',
        ],
        afterItems: '\ub2f9\uc0ac\ub294 \uadc0\ud558\uc758 \ub370\uc774\ud130\ub97c \ub2e4\ub978 \uc81c3\uc790\uc5d0\uac8c \ud310\ub9e4, \uacf5\uc720 \ub610\ub294 \uc81c\uacf5\ud558\uc9c0 \uc54a\uc2b5\ub2c8\ub2e4.',
      },
      {
        title: '6. \ub370\uc774\ud130 \ubcf4\uc720',
        paragraphs: ['\uacc4\uc815\uc774 \ud65c\uc131 \uc0c1\ud0dc\uc778 \ub3d9\uc548\ub9cc \uc138\uc158 \ub370\uc774\ud130\ub97c \ubcf4\uc720\ud569\ub2c8\ub2e4. \ub85c\uadf8\uc544\uc6c3\ud558\uba74 \uc138\uc158 \ub370\uc774\ud130\ub294 \uc11c\ubc84\uc5d0\uc11c \uc0ad\uc81c\ub429\ub2c8\ub2e4. Google Drive\ub098 \ub85c\uceec \uc800\uc7a5\uc18c\uc758 \uc2dd\ubb3c \ub370\uc774\ud130\ub294 \uadc0\ud558\uc758 \uad00\ub9ac \ud558\uc5d0 \uc788\uc2b5\ub2c8\ub2e4.'],
      },
      {
        title: '7. \ub370\uc774\ud130 \ubcf4\uc548',
        items: [
          '\ubaa8\ub4e0 \ub370\uc774\ud130 \uc804\uc1a1\uc5d0 HTTPS \uc554\ud638\ud654.',
          '\ud06c\ub85c\uc2a4 \uc0ac\uc774\ud2b8 \uc2a4\ud06c\ub9bd\ud305 \ubc29\uc9c0\ub97c \uc704\ud55c \ucf58\ud150\uce20 \ubcf4\uc548 \uc815\ucc45(CSP).',
          '\uc545\uc6a9 \ubc29\uc9c0\ub97c \uc704\ud55c \uc18d\ub3c4 \uc81c\ud55c.',
          'OAuth \ud1a0\ud070\uc758 \uc554\ud638\ud654 \uc800\uc7a5.',
        ],
      },
      {
        title: '8. \uadc0\ud558\uc758 \uad8c\ub9ac',
        items: [
          '\uc0dd\uc131\ud55c \uc2dd\ubb3c \ub370\uc774\ud130\uc758 \uc811\uadfc, \uc218\uc815 \ub610\ub294 \uc0ad\uc81c.',
          'Google \uacc4\uc815 \uc124\uc815\uc744 \ud1b5\ud574 \uc5b8\uc81c\ub4e0\uc9c0 Google Drive \uc561\uc138\uc2a4 \ud574\uc81c.',
          '\uc5b8\uc81c\ub4e0\uc9c0 \uc138\uc158 \uc0ad\uc81c \ubc0f \ub85c\uadf8\uc544\uc6c3.',
          '\uac1c\uc778\uc815\ubcf4 \uc81c\uacf5 \uc5c6\uc774 \ub85c\uceec \ubaa8\ub4dc\ub85c \uc571 \uc0ac\uc6a9.',
        ],
      },
      {
        title: '9. \uc544\ub3d9 \uac1c\uc778\uc815\ubcf4',
        paragraphs: ['\ubcf8 \uc571\uc740 13\uc138 \ubbf8\ub9cc \uc544\ub3d9\uc744 \ub300\uc0c1\uc73c\ub85c \ud558\uc9c0 \uc54a\uc2b5\ub2c8\ub2e4. 13\uc138 \ubbf8\ub9cc \uc544\ub3d9\uc758 \uc815\ubcf4\ub97c \uc758\ub3c4\uc801\uc73c\ub85c \uc218\uc9d1\ud558\uc9c0 \uc54a\uc2b5\ub2c8\ub2e4.'],
      },
      {
        title: '10. \ubcf8 \ubc29\uce68\uc758 \ubcc0\uacbd',
        paragraphs: ['\ubcf8 \uac1c\uc778\uc815\ubcf4 \ucc98\ub9ac\ubc29\uce68\uc740 \uc218\uc2dc\ub85c \uc5c5\ub370\uc774\ud2b8\ub420 \uc218 \uc788\uc2b5\ub2c8\ub2e4. \uc5c5\ub370\uc774\ud2b8 \uc2dc \uc0c1\ub2e8\uc758 "\uc2dc\ud589\uc77c"\uc774 \ubcc0\uacbd\ub429\ub2c8\ub2e4. \uc571\uc758 \uacc4\uc18d \uc0ac\uc6a9\uc740 \uac1c\uc815\ub41c \ubc29\uce68\uc5d0 \ub300\ud55c \ub3d9\uc758\ub85c \uac04\uc8fc\ub429\ub2c8\ub2e4.'],
      },
      {
        title: '11. \ubb38\uc758',
        paragraphs: ['\ubcf8 \uac1c\uc778\uc815\ubcf4 \ucc98\ub9ac\ubc29\uce68\uc5d0 \ub300\ud574 \ubb38\uc758\uc0ac\ud56d\uc774 \uc788\uc73c\uc2dc\uba74 \uc560\ud50c\ub9ac\ucf00\uc774\uc158\uc744 \ud1b5\ud574 \uc5f0\ub77d\ud574 \uc8fc\uc138\uc694.'],
      },
    ],
  },
  zh: {
    title: '\u9690\u79c1\u653f\u7b56',
    effectiveDate: '\u751f\u6548\u65e5\u671f: 2026\u5e743\u67084\u65e5',
    intro: '\u6211\u7684\u690d\u7269\u6e05\u5355\uff08\u201c\u672c\u5e94\u7528\u201d\uff09\u81f4\u529b\u4e8e\u4fdd\u62a4\u60a8\u7684\u9690\u79c1\u3002\u672c\u9690\u79c1\u653f\u7b56\u8bf4\u660e\u6211\u4eec\u5982\u4f55\u6536\u96c6\u3001\u4f7f\u7528\u548c\u4fdd\u62a4\u60a8\u7684\u4fe1\u606f\u3002',
    sections: [
      {
        title: '1. \u6211\u4eec\u6536\u96c6\u7684\u4fe1\u606f',
        paragraphs: ['\u6211\u4eec\u6536\u96c6\u63d0\u4f9b\u5e94\u7528\u529f\u80fd\u6240\u9700\u7684\u6700\u5c11\u4fe1\u606f\uff1a'],
        items: [
          '<strong>Google\u8d26\u6237\u4fe1\u606f\uff1a</strong>\u4f7f\u7528Google\u767b\u5f55\u65f6\uff0c\u6211\u4eec\u4f1a\u63a5\u6536\u60a8\u7684\u59d3\u540d\u3001\u7535\u5b50\u90ae\u4ef6\u5730\u5740\u548c\u5934\u50cf\uff0c\u7528\u4e8e\u8eab\u4efd\u9a8c\u8bc1\u548c\u4f1a\u8bdd\u7ba1\u7406\u3002',
          '<strong>Google Drive OAuth\u4ee4\u724c\uff1a</strong>\u5982\u679c\u60a8\u8fde\u63a5Google Drive\uff0c\u6211\u4eec\u4f1a\u5728\u670d\u52a1\u5668\u4e0a\u5b58\u50a8\u52a0\u5bc6\u7684OAuth\u4ee4\u724c\u4ee5\u7ef4\u6301\u60a8\u7684Drive\u8fde\u63a5\u3002',
          '<strong>\u5e94\u7528\u8bbe\u7f6e\uff1a</strong>\u60a8\u7684\u663e\u793a\u8bbe\u7f6e\u5b58\u50a8\u5728\u6d4f\u89c8\u5668\u672c\u5730\u5b58\u50a8\u548c\u6211\u4eec\u7684\u670d\u52a1\u5668\u4e0a\u3002',
        ],
      },
      {
        title: '2. \u6211\u4eec\u4e0d\u6536\u96c6\u7684\u4fe1\u606f',
        paragraphs: ['\u6211\u4eec\u4e0d\u6536\u96c6\u3001\u5b58\u50a8\u6216\u8bbf\u95ee\uff1a'],
        items: [
          '\u60a8\u5728\u5e94\u7528\u4e2d\u521b\u5efa\u7684\u690d\u7269\u6570\u636e\u3001\u7167\u7247\u6216\u4efb\u4f55\u5185\u5bb9\u3002',
          '"PlantInventory"\u6587\u4ef6\u5939\u4e4b\u5916\u7684Google Drive\u5185\u5bb9\u3002',
          '\u60a8\u7684\u6d4f\u89c8\u5386\u53f2\u3001\u4f4d\u7f6e\u3001\u8054\u7cfb\u4eba\u6216\u4efb\u4f55\u5176\u4ed6\u4e2a\u4eba\u6570\u636e\u3002',
        ],
      },
      {
        title: '3. \u6570\u636e\u5b58\u50a8\u4f4d\u7f6e',
        paragraphs: [
          '<strong>Google Drive\u6a21\u5f0f\uff1a</strong>\u6240\u6709\u690d\u7269\u6570\u636e\u548c\u7167\u7247\u4ec5\u5b58\u50a8\u5728\u60a8\u7684\u4e2a\u4ebaGoogle Drive\u8d26\u6237\u4e2d\u3002\u6211\u4eec\u4ece\u4e0d\u5728\u670d\u52a1\u5668\u4e0a\u590d\u5236\u6216\u4fdd\u7559\u8fd9\u4e9b\u6570\u636e\u3002',
          '<strong>\u672c\u5730\u6a21\u5f0f\uff1a</strong>\u6240\u6709\u6570\u636e\u5b58\u50a8\u5728\u6d4f\u89c8\u5668\u7684\u672c\u5730\u5b58\u50a8\uff08IndexedDB\uff09\u4e2d\u3002\u9664\u4f7f\u7528AI\u8bc6\u522b\uff08\u5c06\u7167\u7247\u53d1\u9001\u5230Google Gemini API\uff09\u5916\uff0c\u6570\u636e\u4e0d\u4f1a\u79bb\u5f00\u60a8\u7684\u8bbe\u5907\u3002',
        ],
      },
      {
        title: '4. \u6211\u4eec\u5982\u4f55\u4f7f\u7528\u60a8\u7684\u4fe1\u606f',
        items: [
          '\u9a8c\u8bc1\u60a8\u7684\u8eab\u4efd\u5e76\u7ba1\u7406\u60a8\u7684\u4f1a\u8bdd\u3002',
          '\u8fde\u63a5\u60a8\u7684Google Drive\u4ee5\u4ee3\u8868\u60a8\u8bfb\u5199\u690d\u7269\u6570\u636e\u3002',
          '\u5c06\u7167\u7247\u53d1\u9001\u5230Google Gemini AI\u8fdb\u884c\u690d\u7269\u8bc6\u522b\uff08\u4ec5\u5728\u60a8\u660e\u786e\u8bf7\u6c42\u65f6\uff09\u3002',
        ],
      },
      {
        title: '5. \u7b2c\u4e09\u65b9\u670d\u52a1',
        paragraphs: ['\u672c\u5e94\u7528\u4e0e\u4ee5\u4e0b\u670d\u52a1\u96c6\u6210\uff1a'],
        items: [
          '<strong>Google Sign-In</strong> \u2014 \u7528\u4e8e\u8eab\u4efd\u9a8c\u8bc1\u3002',
          '<strong>Google Drive API</strong> \u2014 \u7528\u4e8e\u6570\u636e\u5b58\u50a8\u3002',
          '<strong>Google Gemini AI</strong> \u2014 \u7528\u4e8e\u690d\u7269\u8bc6\u522b\u3002',
        ],
        afterItems: '\u6211\u4eec\u4e0d\u4f1a\u5411\u4efb\u4f55\u5176\u4ed6\u7b2c\u4e09\u65b9\u51fa\u552e\u3001\u5171\u4eab\u6216\u63d0\u4f9b\u60a8\u7684\u6570\u636e\u3002',
      },
      {
        title: '6. \u6570\u636e\u4fdd\u7559',
        paragraphs: ['\u6211\u4eec\u4ec5\u5728\u60a8\u7684\u8d26\u6237\u5904\u4e8e\u6d3b\u52a8\u72b6\u6001\u65f6\u4fdd\u7559\u60a8\u7684\u4f1a\u8bdd\u6570\u636e\u3002\u6ce8\u9500\u540e\uff0c\u4f1a\u8bdd\u6570\u636e\u5c06\u4ece\u670d\u52a1\u5668\u4e0a\u5220\u9664\u3002Google Drive\u6216\u672c\u5730\u5b58\u50a8\u4e2d\u7684\u690d\u7269\u6570\u636e\u59cb\u7ec8\u7531\u60a8\u63a7\u5236\u3002'],
      },
      {
        title: '7. \u6570\u636e\u5b89\u5168',
        items: [
          '\u6240\u6709\u6570\u636e\u4f20\u8f93\u5747\u4f7f\u7528HTTPS\u52a0\u5bc6\u3002',
          '\u5185\u5bb9\u5b89\u5168\u7b56\u7565\uff08CSP\uff09\u9632\u6b62\u8de8\u7ad9\u811a\u672c\u653b\u51fb\u3002',
          '\u901f\u7387\u9650\u5236\u4ee5\u9632\u6b62\u6ee5\u7528\u3002',
          'OAuth\u4ee4\u724c\u52a0\u5bc6\u5b58\u50a8\u3002',
        ],
      },
      {
        title: '8. \u60a8\u7684\u6743\u5229',
        items: [
          '\u8bbf\u95ee\u3001\u4fee\u6539\u6216\u5220\u9664\u60a8\u521b\u5efa\u7684\u4efb\u4f55\u690d\u7269\u6570\u636e\u3002',
          '\u968f\u65f6\u901a\u8fc7Google\u8d26\u6237\u8bbe\u7f6e\u65ad\u5f00Google Drive\u8bbf\u95ee\u3002',
          '\u968f\u65f6\u5220\u9664\u4f1a\u8bdd\u5e76\u6ce8\u9500\u3002',
          '\u65e0\u9700\u63d0\u4f9b\u4efb\u4f55\u4e2a\u4eba\u4fe1\u606f\u5373\u53ef\u4f7f\u7528\u672c\u5730\u6a21\u5f0f\u3002',
        ],
      },
      {
        title: '9. \u513f\u7ae5\u9690\u79c1',
        paragraphs: ['\u672c\u5e94\u7528\u4e0d\u9488\u5bf913\u5c81\u4ee5\u4e0b\u513f\u7ae5\u3002\u6211\u4eec\u4e0d\u4f1a\u6545\u610f\u6536\u96c613\u5c81\u4ee5\u4e0b\u513f\u7ae5\u7684\u4fe1\u606f\u3002'],
      },
      {
        title: '10. \u672c\u653f\u7b56\u7684\u53d8\u66f4',
        paragraphs: ['\u6211\u4eec\u53ef\u80fd\u4f1a\u4e0d\u65f6\u66f4\u65b0\u672c\u9690\u79c1\u653f\u7b56\u3002\u66f4\u65b0\u65f6\uff0c\u6211\u4eec\u5c06\u66f4\u65b0\u9876\u90e8\u7684\u201c\u751f\u6548\u65e5\u671f\u201d\u3002\u7ee7\u7eed\u4f7f\u7528\u672c\u5e94\u7528\u5373\u8868\u793a\u63a5\u53d7\u4fee\u8ba2\u540e\u7684\u653f\u7b56\u3002'],
      },
      {
        title: '11. \u8054\u7cfb\u6211\u4eec',
        paragraphs: ['\u5982\u679c\u60a8\u5bf9\u672c\u9690\u79c1\u653f\u7b56\u6709\u4efb\u4f55\u7591\u95ee\uff0c\u8bf7\u901a\u8fc7\u5e94\u7528\u7a0b\u5e8f\u4e0e\u6211\u4eec\u8054\u7cfb\u3002'],
      },
    ],
  },
};

export default privacyContent;
